import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Divider } from "#components/atoms/Divider/Divider";
import { Modal } from "#components/molecules/Modal/Modal";
import { useUpdatePart } from "#modules/parts/view/useUpdatePart";
import {
  AddFeatureFormState,
  buildFeatureFromAddForm,
  changeHoleSubType,
  DEFAULT_ADD_FEATURE_FORM,
  featureCategories,
  HoleSubType,
  holeSubTypes,
  usesDiameter,
  usesPerimeter,
} from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/Features/featureForm";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { SelectInput } from "#shared/view/designSystem/SelectInput";
import { FormatedFeatureType } from "shared";

type Props = {
  partId: string;
  features: FormatedFeatureType[];
  opened: boolean;
  onClose: () => void;
};

export const UpdateFeaturesModal = ({ partId, features, opened, onClose }: Props) => {
  const { t } = useTranslation();
  const [editedFeatures, setEditedFeatures] = useState<FormatedFeatureType[]>(features);
  const [addForm, setAddForm] = useState<AddFeatureFormState>(DEFAULT_ADD_FEATURE_FORM);
  const { updatePartAsync, isPending } = useUpdatePart(partId, {
    onSuccess: onClose,
  });

  useEffect(() => {
    if (opened) {
      setEditedFeatures(features);
      setAddForm(DEFAULT_ADD_FEATURE_FORM);
    }
  }, [features, opened]);

  const indexedFeatures = editedFeatures.map((feature, index) => ({ feature, index }));
  const foldingFeatures = indexedFeatures.filter(isFeatureOfType("Folding"));
  const holeFeatures = indexedFeatures.filter(isFeatureOfType("Hole"));
  const sideFeatures = indexedFeatures.filter(isFeatureOfType("Side"));

  const updateFeature = (index: number, nextFeature: FormatedFeatureType) => {
    setEditedFeatures((current) =>
      current.map((feature, featureIndex) => (featureIndex === index ? nextFeature : feature)),
    );
  };

  const handleDelete = (index: number) => {
    setEditedFeatures((current) => current.filter((_, featureIndex) => featureIndex !== index));
  };

  const handleAdd = () => {
    const feature = buildFeatureFromAddForm(addForm);
    if (!feature) {
      return;
    }

    setEditedFeatures((current) => [...current, feature]);
    setAddForm(DEFAULT_ADD_FEATURE_FORM);
  };

  const handleValidate = async () => {
    await updatePartAsync({
      id: partId,
      payload: { formattedFeaturesJsonOverride: editedFeatures },
    });
  };

  const isFoldingLastSection = holeFeatures.length === 0 && sideFeatures.length === 0;
  const isHoleLastSection = sideFeatures.length === 0;

  return (
    <Modal
      opened={opened}
      title={t("part.features.editTitle")}
      primaryText={t("common.validate")}
      primaryDisabled={isPending}
      onPrimary={handleValidate}
      primaryLoading={isPending}
      closeText={t("common.cancel")}
      onClose={onClose}
    >
      <Stack gap="md">
        {foldingFeatures.length > 0 && (
          <FeatureSection title={t("part.features.sections.folding")} isLast={isFoldingLastSection}>
            {foldingFeatures.map(({ feature, index }) => (
              <FoldingRow
                key={`${feature.type}-${index}`}
                feature={feature}
                onAngleChange={(angle) => updateFeature(index, { ...feature, angle })}
                onCountChange={(count) => updateFeature(index, { ...feature, count })}
                onDelete={() => handleDelete(index)}
              />
            ))}
          </FeatureSection>
        )}

        {holeFeatures.length > 0 && (
          <FeatureSection title={t("part.features.sections.holes")} isLast={isHoleLastSection}>
            {holeFeatures.map(({ feature, index }) => (
              <HoleRow
                key={`${feature.type}-${feature.subType}-${index}`}
                feature={feature}
                onSubTypeChange={(subType) =>
                  updateFeature(index, changeHoleSubType(feature, subType))
                }
                onFeatureChange={(nextFeature) => updateFeature(index, nextFeature)}
                onDelete={() => handleDelete(index)}
              />
            ))}
          </FeatureSection>
        )}

        {sideFeatures.length > 0 && (
          <FeatureSection title={t("part.features.sections.sides")} isLast={true}>
            {sideFeatures.map(({ feature, index }) => (
              <SideRow
                key={`${feature.type}-${index}`}
                feature={feature}
                onLengthChange={(length) => updateFeature(index, { ...feature, length })}
                onCountChange={(count) => updateFeature(index, { ...feature, count })}
                onDelete={() => handleDelete(index)}
              />
            ))}
          </FeatureSection>
        )}

        <AddFeatureForm
          form={addForm}
          onChange={setAddForm}
          onAdd={handleAdd}
          canAdd={buildFeatureFromAddForm(addForm) !== null}
        />
      </Stack>
    </Modal>
  );
};

const FeatureSection = ({
  title,
  children,
  isLast,
}: {
  title: string;
  children: ReactNode;
  isLast: boolean;
}) => {
  return (
    <Stack gap="xs">
      <Text fw={600}>{title}</Text>
      {children}
      {!isLast && <Divider type="secondary" style={{ marginTop: spacing[4] }} />}
    </Stack>
  );
};

const FoldingRow = ({
  feature,
  onAngleChange,
  onCountChange,
  onDelete,
}: {
  feature: Extract<FormatedFeatureType, { type: "Folding" }>;
  onAngleChange: (angle: number) => void;
  onCountChange: (count: number) => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <FeatureRow onDelete={onDelete}>
      <NumberInput
        label={t("part.features.fields.angle")}
        value={feature.angle ?? ""}
        min={0}
        allowNegative={false}
        suffix=" °"
        style={{ flex: 1 }}
        onChange={onAngleChange}
      />
      <CountInput value={feature.count} onChange={onCountChange} />
    </FeatureRow>
  );
};

const HoleRow = ({
  feature,
  onSubTypeChange,
  onFeatureChange,
  onDelete,
}: {
  feature: Extract<FormatedFeatureType, { type: "Hole" }>;
  onSubTypeChange: (subType: HoleSubType) => void;
  onFeatureChange: (feature: FormatedFeatureType) => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <FeatureRow onDelete={onDelete}>
      <HoleTypeSelect value={feature.subType} onChange={onSubTypeChange} />
      {feature.subType === "hole_circular" || feature.subType === "hole_slotted" ? (
        <NumberInput
          label={t("part.features.fields.diameter")}
          value={feature.diameter}
          min={0}
          allowNegative={false}
          unit="mm"
          style={{ flex: 1 }}
          onChange={(diameter) => onFeatureChange({ ...feature, diameter })}
        />
      ) : null}
      {feature.subType === "hole_complex" || feature.subType === "hole_folded" ? (
        <NumberInput
          label={t("part.features.fields.perimeter")}
          value={feature.edgeLength}
          min={0}
          allowNegative={false}
          unit="mm"
          style={{ flex: 1 }}
          onChange={(edgeLength) => onFeatureChange({ ...feature, edgeLength })}
        />
      ) : null}
      <CountInput
        value={feature.count}
        onChange={(count) => onFeatureChange({ ...feature, count })}
      />
    </FeatureRow>
  );
};

const SideRow = ({
  feature,
  onLengthChange,
  onCountChange,
  onDelete,
}: {
  feature: Extract<FormatedFeatureType, { type: "Side" }>;
  onLengthChange: (length: number) => void;
  onCountChange: (count: number) => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <FeatureRow onDelete={onDelete}>
      <NumberInput
        label={t("part.features.fields.length")}
        value={feature.length}
        min={0}
        allowNegative={false}
        unit="mm"
        style={{ flex: 1 }}
        onChange={onLengthChange}
      />
      <CountInput value={feature.count} onChange={onCountChange} />
    </FeatureRow>
  );
};

const AddFeatureForm = ({
  form,
  onChange,
  onAdd,
  canAdd,
}: {
  form: AddFeatureFormState;
  onChange: (form: AddFeatureFormState) => void;
  onAdd: () => void;
  canAdd: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Box bg={colors.bg.elevation.secondary} p={spacing[4]} bdrs={radius.md}>
      <Stack gap="xs">
        <Text fw={600}>{t("part.features.add")}</Text>
        <Flex gap="xs" align="flex-end">
          <SelectInput
            label={t("part.features.fields.category")}
            data={featureCategories.map((type) => ({
              value: type,
              label: t(`part.features.categories.${type}`),
            }))}
            value={form.type}
            allowDeselect={false}
            style={{ flex: 1 }}
            onChange={(value) => {
              if (!value) {
                return;
              }

              onChange({ ...form, type: value as AddFeatureFormState["type"] });
            }}
          />
          {form.type === "Hole" && (
            <HoleTypeSelect
              value={form.holeSubType}
              label={t("part.features.fields.holeType")}
              onChange={(holeSubType) => onChange({ ...form, holeSubType })}
            />
          )}
        </Flex>
        <Flex gap="xs" align="flex-end">
          {form.type === "Folding" && (
            <NumberInput
              label={t("part.features.fields.angle")}
              value={form.angle ?? ""}
              min={0}
              allowNegative={false}
              suffix=" °"
              placeholder={t("part.features.placeholders.angle")}
              style={{ flex: 1 }}
              onChange={(angle) => onChange({ ...form, angle })}
            />
          )}
          {form.type === "Hole" && usesDiameter(form.holeSubType) && (
            <NumberInput
              label={t("part.features.fields.diameter")}
              value={form.diameter ?? ""}
              min={0}
              allowNegative={false}
              unit="mm"
              placeholder={t("part.features.placeholders.measure")}
              style={{ flex: 1 }}
              onChange={(diameter) => onChange({ ...form, diameter })}
            />
          )}
          {form.type === "Hole" && usesPerimeter(form.holeSubType) && (
            <NumberInput
              label={t("part.features.fields.perimeter")}
              value={form.edgeLength ?? ""}
              min={0}
              allowNegative={false}
              unit="mm"
              placeholder={t("part.features.placeholders.measure")}
              style={{ flex: 1 }}
              onChange={(edgeLength) => onChange({ ...form, edgeLength })}
            />
          )}
          {form.type === "Side" && (
            <NumberInput
              label={t("part.features.fields.length")}
              value={form.length ?? ""}
              min={0}
              allowNegative={false}
              unit="mm"
              placeholder={t("part.features.placeholders.measure")}
              style={{ flex: 1 }}
              onChange={(length) => onChange({ ...form, length })}
            />
          )}
          <CountInput
            value={form.count ?? ""}
            placeholder={t("part.features.placeholders.count")}
            onChange={(count) => onChange({ ...form, count })}
          />
        </Flex>
        <Flex justify="end">
          <Button onClick={onAdd} disabled={!canAdd} variant="tertiary">
            {t("common.add")}
          </Button>
        </Flex>
      </Stack>
    </Box>
  );
};

const FeatureRow = ({ children, onDelete }: { children: ReactNode; onDelete: () => void }) => {
  const { t } = useTranslation();

  return (
    <Flex gap="xs" align="flex-end">
      {children}
      <ActionIcon
        size="sm"
        variant="subtle"
        c="dimmed"
        aria-label={t("common.delete")}
        onClick={onDelete}
      >
        <Icon name="trash" size={16} />
      </ActionIcon>
    </Flex>
  );
};

const CountInput = ({
  value,
  onChange,
  placeholder,
}: {
  value: number | string;
  onChange: (count: number) => void;
  placeholder?: string;
}) => {
  const { t } = useTranslation();

  return (
    <NumberInput
      label={t("part.features.fields.count")}
      value={value}
      min={0}
      allowNegative={false}
      decimalScale={0}
      placeholder={placeholder}
      style={{ flex: 1 }}
      onChange={onChange}
    />
  );
};

const HoleTypeSelect = ({
  value,
  onChange,
  label,
}: {
  value: HoleSubType;
  onChange: (subType: HoleSubType) => void;
  label?: string;
}) => {
  const { t } = useTranslation();

  return (
    <SelectInput
      label={label ?? t("part.features.fields.type")}
      data={holeSubTypes.map((subType) => ({
        value: subType,
        label: t(`part.features.holeTypes.${subType}`),
      }))}
      value={value}
      allowDeselect={false}
      style={{ flex: 1 }}
      onChange={(nextValue) => {
        if (!nextValue) {
          return;
        }

        onChange(nextValue as HoleSubType);
      }}
    />
  );
};

const isFeatureOfType =
  <T extends FormatedFeatureType["type"]>(type: T) =>
  (
    item: IndexedFeature,
  ): item is { feature: Extract<FormatedFeatureType, { type: T }>; index: number } =>
    item.feature.type === type;

type IndexedFeature = { feature: FormatedFeatureType; index: number };
